import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Table, Badge,
  Spinner, Alert, Modal, Form, Nav
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTrainingAuth } from '../../context/TrainingAuthContext';
import TrainingNavbar from '../../components/TrainingNavbar';
import trainingService from '../../services/trainingService';
import usePagination from '../../hooks/usePagination';
import TablePagination from '../../components/TablePagination';

const STATUS_CONFIG = {
  draft:     { label: 'Draft',             bg: 'secondary' },
  submitted: { label: 'Pending Review',    bg: 'primary'   },
  sent_back: { label: 'Sent Back',         bg: 'warning'   },
  approved:  { label: 'Approved',          bg: 'success'   },
};

const ROLE_LABEL = {
  training_reviewer: 'Training Reviewer',
  training_admin: 'Training Admin',
};

const TrainingReviewerDashboard = () => {
  const { trainingUser } = useTrainingAuth();
  const navigate = useNavigate();

  const [submissions, setSubmissions] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('pending');

  // Send-back modal
  const [sendBackModal, setSendBackModal] = useState(false);
  const [sendBackId, setSendBackId] = useState(null);
  const [sendBackComment, setSendBackComment] = useState('');
  const [sendBackLoading, setSendBackLoading] = useState(false);
  const [sendBackError, setSendBackError] = useState('');

  // Approve modal
  const [approveModal, setApproveModal] = useState(false);
  const [approveId, setApproveId] = useState(null);
  const [approveClientId, setApproveClientId] = useState('');
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState('');

  const [actionSuccess, setActionSuccess] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [subData, clientData] = await Promise.all([
        trainingService.listSubmissions(),
        trainingService.getClients(),
      ]);
      if (subData.success) setSubmissions(subData.submissions);
      else setError(subData.message || 'Failed to load submissions');
      if (clientData.success) setClients(clientData.clients);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const pending = submissions.filter(s => s.current_status === 'submitted');
  const history = submissions.filter(s => s.current_status !== 'submitted');

  const tabData = activeTab === 'pending' ? pending : history;

  const { page, setPage, totalPages, paginatedItems: paginatedTabData } = usePagination(tabData);

  useEffect(() => {
    setPage(1);
  }, [activeTab, setPage]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso + (iso.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('en-GB');
  };

  // --- Send Back ---
  const openSendBack = (id) => {
    setSendBackId(id);
    setSendBackComment('');
    setSendBackError('');
    setSendBackModal(true);
  };

  const handleSendBack = async () => {
    if (!sendBackComment.trim()) {
      setSendBackError('Please enter comments explaining what needs to be corrected.');
      return;
    }
    setSendBackLoading(true);
    setSendBackError('');
    try {
      const result = await trainingService.sendBack(sendBackId, sendBackComment);
      if (result.success) {
        setSendBackModal(false);
        setActionSuccess('Submission sent back for corrections.');
        loadData();
      } else {
        setSendBackError(result.message || 'Failed');
      }
    } catch (err) {
      setSendBackError(err.response?.data?.message || 'Failed to send back');
    } finally {
      setSendBackLoading(false);
    }
  };

  // --- Approve ---
  const openApprove = (id) => {
    setApproveId(id);
    setApproveClientId('');
    setApproveError('');
    setApproveModal(true);
  };

  const handleApprove = async () => {
    if (!approveClientId) {
      setApproveError('Please select a training client.');
      return;
    }
    setApproveLoading(true);
    setApproveError('');
    try {
      const result = await trainingService.approve(approveId, parseInt(approveClientId));
      if (result.success) {
        setApproveModal(false);
        setActionSuccess('Submission approved! PDF generated and emailed to all recipients.');
        loadData();
      } else {
        setApproveError(result.message || 'Approval failed');
      }
    } catch (err) {
      setApproveError(err.response?.data?.message || 'Approval failed');
    } finally {
      setApproveLoading(false);
    }
  };

  return (
    <>
      <TrainingNavbar />
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="mb-0">
              <i className="fas fa-clipboard-check text-success me-2"></i>
              Review Dashboard
            </h2>
            <p className="text-muted mb-0">
              {ROLE_LABEL[trainingUser?.role] || trainingUser?.role} — {trainingUser?.full_name}
            </p>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="outline-success" onClick={() => navigate('/training/reports')}>
              <i className="fas fa-chart-bar me-2"></i>View Reports &amp; Charts
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={loadData}>
              <i className="fas fa-sync me-1"></i>Refresh
            </Button>
          </Col>
        </Row>

        {/* Stats */}
        <Row className="mb-4 g-3">
          {[
            { label: 'Pending Review', count: pending.length, color: 'primary' },
            { label: 'Total Processed', count: history.length, color: 'secondary' },
            { label: 'Approved', count: submissions.filter(s => s.current_status === 'approved').length, color: 'success' },
            { label: 'Sent Back', count: submissions.filter(s => s.current_status === 'sent_back').length, color: 'warning' },
          ].map(({ label, count, color }) => (
            <Col key={label} xs={6} md={3}>
              <Card className="border-0 shadow-sm text-center h-100">
                <Card.Body>
                  <h3 className={`text-${color} mb-1`}>{count}</h3>
                  <small className="text-muted">{label}</small>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
        {actionSuccess && (
          <Alert variant="success" dismissible onClose={() => setActionSuccess('')}>
            <i className="fas fa-check-circle me-2"></i>{actionSuccess}
          </Alert>
        )}

        {/* Tabs */}
        <Card className="shadow-sm">
          <Card.Header className="bg-white">
            <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab}>
              <Nav.Item>
                <Nav.Link eventKey="pending">
                  Pending Review
                  {pending.length > 0 && (
                    <Badge bg="primary" className="ms-2">{pending.length}</Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="history">Processed</Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="success" />
              </div>
            ) : tabData.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-inbox fa-3x mb-3 d-block opacity-25"></i>
                {activeTab === 'pending' ? 'No submissions pending review.' : 'No processed submissions yet.'}
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Course</th>
                      <th>Trainee</th>
                      <th>Submitted By</th>
                      <th>Cert No</th>
                      <th>Issue Date</th>
                      <th>Status</th>
                      {activeTab === 'pending' && <th>Actions</th>}
                      {activeTab === 'history' && <th>Reviewed By</th>}
                      {activeTab === 'history' && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTabData.map(sub => {
                      const statusCfg = STATUS_CONFIG[sub.current_status] || { label: sub.current_status || 'Unknown', bg: 'secondary' };
                      return (
                        <tr key={sub.id}>
                          <td>
                            <div className="fw-semibold">{sub.courseName}</div>
                            <small className="text-muted">{sub.courseCode}</small>
                          </td>
                          <td>{sub.traineeName}</td>
                          <td><small>{sub.submitterName}</small></td>
                          <td><small className="text-muted">{sub.certNo || '—'}</small></td>
                          <td><small>{formatDate(sub.issueDate)}</small></td>
                          <td>
                            <Badge bg={statusCfg.bg} text={statusCfg.bg === 'warning' ? 'dark' : undefined}>
                              {statusCfg.label}
                            </Badge>
                            {sub.hasPdf && (
                              <Badge bg="light" text="dark" className="ms-1">
                                <i className="fas fa-file-pdf text-success"></i>
                              </Badge>
                            )}
                          </td>
                          {activeTab === 'pending' && (
                            <td>
                              <div className="d-flex gap-1 flex-wrap">
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => navigate(`/training/submissions/${sub.id}`)}
                                >
                                  <i className="fas fa-eye me-1"></i>View
                                </Button>
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => openApprove(sub.id)}
                                >
                                  <i className="fas fa-check me-1"></i>Approve
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => openSendBack(sub.id)}
                                >
                                  <i className="fas fa-undo me-1"></i>Send Back
                                </Button>
                              </div>
                            </td>
                          )}
                          {activeTab === 'history' && (
                            <td>
                              <small>{sub.reviewerName || '—'}</small>
                            </td>
                          )}
                          {activeTab === 'history' && (
                            <td>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => navigate(`/training/submissions/${sub.id}`)}
                              >
                                <i className="fas fa-eye me-1"></i>View
                              </Button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <TablePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Send Back Modal */}
      <Modal show={sendBackModal} onHide={() => setSendBackModal(false)} centered>
        <Modal.Header closeButton className="border-danger">
          <Modal.Title className="text-danger">
            <i className="fas fa-undo me-2"></i>Send Back for Corrections
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sendBackError && <Alert variant="danger">{sendBackError}</Alert>}
          <Form.Group>
            <Form.Label>Comments <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Describe what needs to be corrected..."
              value={sendBackComment}
              onChange={(e) => setSendBackComment(e.target.value)}
            />
            <Form.Text className="text-muted">
              The coordinator will see these comments and make corrections before resubmitting.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setSendBackModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleSendBack} disabled={sendBackLoading}>
            {sendBackLoading
              ? <><Spinner size="sm" className="me-2" />Sending...</>
              : <><i className="fas fa-undo me-2"></i>Send Back</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Approve Modal */}
      <Modal show={approveModal} onHide={() => setApproveModal(false)} centered>
        <Modal.Header closeButton className="border-success">
          <Modal.Title className="text-success">
            <i className="fas fa-check-circle me-2"></i>Approve & Generate Certificate
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {approveError && <Alert variant="danger">{approveError}</Alert>}
          <Alert variant="info" className="mb-3">
            <i className="fas fa-info-circle me-2"></i>
            After approval, the PDF certificate will be generated and emailed to all Training Admins and the selected client.
          </Alert>
          <Form.Group>
            <Form.Label>Select Training Client <span className="text-danger">*</span></Form.Label>
            <Form.Select
              value={approveClientId}
              onChange={(e) => setApproveClientId(e.target.value)}
            >
              <option value="">-- Select Client --</option>
              {clients.map(c => (
                <option key={c.training_client_id} value={c.training_client_id}>
                  {c.client_name} ({c.contact_email})
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              The certificate PDF will also be sent to the client's email.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setApproveModal(false)}>Cancel</Button>
          <Button variant="success" onClick={handleApprove} disabled={approveLoading}>
            {approveLoading
              ? <><Spinner size="sm" className="me-2" />Approving...</>
              : <><i className="fas fa-check me-2"></i>Approve & Send</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TrainingReviewerDashboard;
